

export const dynamic = "force-dynamic";

import { useState, useMemo, useEffect, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import DataTable from "../components/DataTable";
import SearchInput from "../components/SearchInput";
import TaskForm from "./TaskForm";
import ImageViewer from "@/components/ImageViewer";
import { getImageUrl } from "@/lib/utils";
import { tasksApi, apiClient } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";

const columns = [
  { key: "pending", label: "Pending", color: "border-t-rose-400", dot: "bg-rose-400", bg: "bg-rose-50" },
  { key: "in_progress", label: "In Progress", color: "border-t-blue-400", dot: "bg-blue-400", bg: "bg-blue-50" },
  { key: "completed", label: "Completed", color: "border-t-emerald-400", dot: "bg-emerald-400", bg: "bg-emerald-50" }
] as const;

type TaskStatus = typeof columns[number]["key"];

const priorityConfig = {
  high: { color: "text-rose-600", bg: "bg-rose-50", icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" },
  medium: { color: "text-amber-600", bg: "bg-amber-50", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
  low: { color: "text-stone-500", bg: "bg-stone-50", icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }
};

interface Task {
  id: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  due_date: string;
  assigned_to: string;
  event_id?: number;
  event?: { id: number; name: string };
  before_image?: string | null;
  after_image?: string | null;
}

function TasksPageContent() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"kanban" | "table">("kanban");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [imageViewer, setImageViewer] = useState<{ url: string; title: string } | null>(null);

  // Fetch tasks from API
  useEffect(() => {
    loadTasks();
  }, [isStaff]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      // Staff only see their own assigned tasks; privileged roles see all
      const response = isStaff ? await tasksApi.getMyTasks() : await tasksApi.list();
      const tasksData = response.data?.data || response.data || [];
      setTasks(tasksData);
    } catch (error) {
      showToast("Failed to load tasks", "error");
      console.error("Error loading tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    if (!searchQuery) return tasks;
    const q = searchQuery.toLowerCase();
    return tasks.filter(t => 
      t.title?.toLowerCase().includes(q) ||
      t.assigned_to?.toLowerCase().includes(q) ||
      t.event?.name?.toLowerCase().includes(q)
    );
  }, [tasks, searchQuery]);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this task?")) return;
    try {
      await tasksApi.delete(id);
      showToast("Task deleted successfully", "success");
      loadTasks();
    } catch (error) {
      showToast("Failed to delete task", "error");
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Append text fields
      formData.append('title', data.title);
      formData.append('type', editingTask?.event ? 'event_specific' : 'daily');
      formData.append('status', data.status || 'pending');
      formData.append('priority', data.priority || 'medium');
      formData.append('assigned_to', data.assignedTo);
      
      if (data.dueDate) {
        formData.append('due_date', data.dueDate);
      }
      
      if (data.notes) {
        formData.append('description', data.notes);
      }

      // Append image files if present
      if (data.before_image instanceof File) {
        formData.append('before_image', data.before_image);
      }
      
      if (data.after_image instanceof File) {
        formData.append('after_image', data.after_image);
      }

      if (editingTask) {
        // For updates, we need to use a custom request with multipart
        await apiClient.post(`/tasks/${editingTask.id}`, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'X-HTTP-Method-Override': 'PUT'
          }
        });
        showToast("Task updated successfully", "success");
      } else {
        await apiClient.post('/tasks', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast("Task created successfully", "success");
      }
      
      setIsModalOpen(false);
      setEditingTask(null);
      loadTasks();
    } catch (error) {
      console.error("Task save error:", error);
      showToast("Failed to save task", "error");
    }
  };

  const handleComplete = async (id: number) => {
    try {
      await tasksApi.complete(id);
      showToast("Task marked as completed", "success");
      loadTasks();
    } catch (error) {
      showToast("Failed to complete task", "error");
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Update task status locally first for UI responsiveness
    const newStatus = destination.droppableId as TaskStatus;
    const taskId = parseInt(draggableId);
    
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, status: newStatus } : t
    ));

    // Update via API
    try {
      await tasksApi.update(taskId, { status: newStatus });
      showToast("Task status updated", "success");
    } catch (error) {
      showToast("Failed to update task status", "error");
      loadTasks(); // Reload to get correct state
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const tableColumns = [
    { key: "title", header: "Task", render: (t: Task) => (
      <div>
        <div className="font-semibold text-stone-800">{t.title}</div>
        <div className="text-xs text-stone-400">{t.event?.name || "-"}</div>
        {/* Task Images in Table */}
        {(t.before_image || t.after_image) && (
          <div className="flex gap-1.5 mt-1.5">
            {t.before_image && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageViewer({
                    url: getImageUrl(t.before_image),
                    title: 'Before Photo',
                  });
                }}
                className="relative group"
              >
                <img
                  src={getImageUrl(t.before_image)}
                  alt="Before"
                  className="w-10 h-10 object-cover rounded border border-stone-200 hover:opacity-90 transition-opacity"
                />
                <span className="absolute -top-1 -left-1 bg-stone-500 text-white text-[7px] px-1 py-0.5 rounded">
                  B
                </span>
              </button>
            )}
            {t.after_image && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageViewer({
                    url: getImageUrl(t.after_image),
                    title: 'After Photo',
                  });
                }}
                className="relative group"
              >
                <img
                  src={getImageUrl(t.after_image)}
                  alt="After"
                  className="w-10 h-10 object-cover rounded border border-stone-200 hover:opacity-90 transition-opacity"
                />
                <span className="absolute -top-1 -left-1 bg-emerald-500 text-white text-[7px] px-1 py-0.5 rounded">
                  A
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    )},
    { key: "assigned_to", header: "Assigned To", render: (t: Task) => t.assigned_to || "-" },
    { key: "due_date", header: "Due Date", render: (t: Task) => (
      <span className={`text-xs ${t.due_date && new Date(t.due_date) < new Date() ? 'text-rose-500' : 'text-stone-600'}`}>
        {t.due_date || "-"}
      </span>
    )},
    { key: "priority", header: "Priority", render: (t: Task) => {
      const config = priorityConfig[t.priority as keyof typeof priorityConfig];
      return (
        <div className={`flex items-center gap-1.5 ${config?.color || 'text-stone-500'}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config?.icon || priorityConfig.medium.icon} />
          </svg>
          <span className="text-xs font-medium capitalize">{t.priority}</span>
        </div>
      );
    }},
    { key: "status", header: "Status", render: (t: Task) => (
      <Badge label={t.status} status={t.status} />
    )},
  ];

  const tableActions = (t: Task) => (
    <div className="flex gap-1.5">
      <button 
        onClick={() => { setEditingTask(t); setIsModalOpen(true); }}
        className="text-xs px-2.5 py-1.5 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 font-medium"
      >
        Edit
      </button>
      {/* Staff cannot delete tasks */}
      {!isStaff && (
        <button 
          onClick={() => handleDelete(t.id)}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-medium"
        >
          Delete
        </button>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-3 text-sm text-stone-500">
          {columns.map(c => {
            const count = tasks.filter(t => t.status === c.key).length;
            return (
              <span key={c.key} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${c.dot}`}></span>
                {c.label} <strong className="text-stone-700">{count}</strong>
              </span>
            );
          })}
        </div>
        <div className="flex gap-2">
          {/* View Toggle */}
          <div className="flex bg-stone-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode("kanban")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === "kanban" ? "bg-white text-stone-700 shadow-sm" : "text-stone-500"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Board
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === "table" ? "bg-white text-stone-700 shadow-sm" : "text-stone-500"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              List
            </button>
          </div>
          <button 
            onClick={() => { setEditingTask(null); setIsModalOpen(true); }}
            className="text-sm font-semibold text-white px-4 py-2 rounded-xl gold-gradient shadow-sm"
          >
            + Add Task
          </button>
        </div>
      </div>

      {/* Search */}
      <SearchInput 
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search tasks by title, assignee, or event..."
        className="max-w-md"
      />

      {/* Kanban View with Drag & Drop */}
      {viewMode === "kanban" && (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {columns.map(col => {
              const colTasks = filteredTasks.filter(t => t.status === col.key);
              return (
                <div key={col.key} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${col.bg}`}>
                      <span className={`w-2 h-2 rounded-full ${col.dot}`}></span>
                      <span className="text-sm font-medium text-stone-700">{col.label}</span>
                      <span className="text-xs text-stone-500 bg-white px-1.5 py-0.5 rounded-full">{colTasks.length}</span>
                    </div>
                  </div>
                  
                  <Droppable droppableId={col.key}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-3 min-h-[200px] p-2 rounded-xl transition-colors ${
                          snapshot.isDraggingOver ? 'bg-stone-50/50 border-2 border-dashed border-amber-300' : ''
                        }`}
                      >
                        {colTasks.map((task, index) => {
                          const priority = priorityConfig[task.priority as keyof typeof priorityConfig];
                          return (
                            <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => { setEditingTask(task); setIsModalOpen(true); }}
                                  className={`bg-white rounded-xl p-4 border border-stone-100 cursor-pointer hover:shadow-md transition-all ${col.color} ${
                                    snapshot.isDragging ? 'shadow-lg ring-2 ring-amber-200 rotate-2' : ''
                                  }`}
                                  style={{
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <h4 className="font-semibold text-stone-800 text-sm">{task.title}</h4>
                                    <svg className={`w-4 h-4 ${priority?.color || 'text-stone-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={priority?.icon || priorityConfig.medium.icon} />
                                    </svg>
                                  </div>
                                  <p className="text-xs text-stone-400 mb-3">{task.event?.name || "-"}</p>
                                  
                                  {/* Task Images */}
                                  {(task.before_image || task.after_image) && (
                                    <div className="mb-3 pt-2 border-t border-stone-100">
                                      <p className="text-[10px] font-medium text-stone-500 mb-2">Photos</p>
                                      <div className="flex gap-2">
                                        {task.before_image && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setImageViewer({
                                                url: getImageUrl(task.before_image),
                                                title: 'Before Photo',
                                              });
                                            }}
                                            className="relative group flex-shrink-0"
                                          >
                                            <img
                                              src={getImageUrl(task.before_image)}
                                              alt="Before"
                                              className="w-16 h-16 object-cover rounded-lg border border-stone-200 hover:opacity-90 transition-opacity"
                                            />
                                            <span className="absolute -top-1 -left-1 bg-stone-500 text-white text-[8px] px-1.5 py-0.5 rounded-md shadow-sm">
                                              Before
                                            </span>
                                          </button>
                                        )}
                                        {task.after_image && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setImageViewer({
                                                url: getImageUrl(task.after_image),
                                                title: 'After Photo',
                                              });
                                            }}
                                            className="relative group flex-shrink-0"
                                          >
                                            <img
                                              src={getImageUrl(task.after_image)}
                                              alt="After"
                                              className="w-16 h-16 object-cover rounded-lg border border-stone-200 hover:opacity-90 transition-opacity"
                                            />
                                            <span className="absolute -top-1 -left-1 bg-emerald-500 text-white text-[8px] px-1.5 py-0.5 rounded-md shadow-sm">
                                              After
                                            </span>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center gap-3 text-xs">
                                    <div className="flex items-center gap-1 text-stone-500">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      {task.assigned_to || "-"}
                                    </div>
                                    <div className={`flex items-center gap-1 ${task.due_date && new Date(task.due_date) < new Date() ? 'text-rose-500' : 'text-stone-400'}`}>
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      {task.due_date || "-"}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                        {colTasks.length === 0 && (
                          <div className="text-center py-8 text-stone-400 text-sm">
                            Drop tasks here
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <DataTable
          data={filteredTasks}
          columns={tableColumns}
          keyExtractor={(t) => String(t.id)}
          onRowClick={(t) => { setEditingTask(t); setIsModalOpen(true); }}
          actions={tableActions}
          emptyMessage="No tasks found."
          searchable={false}
        />
      )}

      {/* Add/Edit Task Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingTask(null); }}
        title={editingTask ? "Edit Task" : "Add New Task"}
        size="md"
      >
        <TaskForm
          initialData={editingTask ? {
            title: editingTask.title,
            event: editingTask.event?.name,
            assignedTo: editingTask.assigned_to,
            dueDate: editingTask.due_date,
            priority: editingTask.priority,
            status: editingTask.status,
          } : undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingTask(null); }}
        />
      </Modal>

      {/* Image Viewer Modal */}
      {imageViewer && (
        <ImageViewer
          imageUrl={imageViewer.url}
          title={imageViewer.title}
          isOpen={!!imageViewer}
          onClose={() => setImageViewer(null)}
        />
      )}
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    </DashboardLayout>
  );
}

export default function TasksPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <TasksPageContent />
    </Suspense>
  );
}
